import React, { Component, ErrorInfo, ReactNode } from "react";

interface Props {
  children: ReactNode;
}

interface State {
  hasError: boolean;
  error: Error | null;
  theme: string;
}

class ErrorBoundary extends Component<Props, State> {
  public state: State = {
    hasError: false,
    error: null,
    theme: "dark",
  };

  public static getDerivedStateFromError(error: Error): State {
    return {
      hasError: true,
      error,
      theme: localStorage.getItem("selectedTheme") || "dark",
    };
  }

  public componentDidCatch(error: Error, errorInfo: ErrorInfo) {
    console.error("Uncaught error:", error, errorInfo);
  }

  public componentDidMount() {
    const theme = localStorage.getItem("selectedTheme") || "dark";
    this.setState({ theme });

    // Listen for theme changes
    const handleStorageChange = () => {
      const newTheme = localStorage.getItem("selectedTheme") || "dark";
      this.setState({ theme: newTheme });
    };

    window.addEventListener("storage", handleStorageChange);

    return () => {
      window.removeEventListener("storage", handleStorageChange);
    };
  }

  private handleReload = () => {
    window.location.reload();
  };

  public render() {
    if (this.state.hasError) {
      const isDark = this.state.theme === "dark";

      return (
        <div
          style={{
            display: "flex",
            flexDirection: "column",
            justifyContent: "center",
            alignItems: "center",
            height: "100vh",
            padding: "20px",
            textAlign: "center",
            backgroundColor: isDark ? "#181A21" : "#FCFCFC",
            fontFamily:
              "AvenirRegular, -apple-system, BlinkMacSystemFont, 'Segoe UI', 'Roboto', 'Oxygen', 'Ubuntu', 'Cantarell', 'Fira Sans', 'Droid Sans', 'Helvetica Neue', sans-serif",
          }}
        >
          <h1
            style={{
              fontSize: "24px",
              marginBottom: "16px",
              color: isDark ? "#EAECEF" : "#1E2329",
            }}
          >
            Oops! Something went wrong
          </h1>
          <p
            style={{
              fontSize: "16px",
              marginBottom: "24px",
              color: isDark ? "#B7BDC6" : "#474D57",
            }}
          >
            We're sorry for the inconvenience. Please try reloading the page.
          </p>
          <button
            onClick={this.handleReload}
            style={{
              padding: "12px 24px",
              fontSize: "16px",
              backgroundColor: "#11BE6A",
              color: "#fff",
              border: "none",
              borderRadius: "4px",
              cursor: "pointer",
              fontWeight: "600",
              maxWidth: "200px",
              margin: "0 auto",
            }}
          >
            Reload Page
          </button>
          {process.env.NODE_ENV === "development" && this.state.error && (
            <details
              style={{
                marginTop: "24px",
                textAlign: "left",
                maxWidth: "600px",
              }}
            >
              <summary
                style={{
                  cursor: "pointer",
                  color: isDark ? "#B7BDC6" : "#474D57",
                  fontSize: "14px",
                }}
              >
                Error Details (Development Only)
              </summary>
              <pre
                style={{
                  marginTop: "12px",
                  padding: "12px",
                  backgroundColor: isDark ? "#2B3139" : "#EAECEF",
                  color: isDark ? "#EAECEF" : "#1E2329",
                  borderRadius: "4px",
                  fontSize: "12px",
                  overflow: "auto",
                }}
              >
                {this.state.error.toString()}
              </pre>
            </details>
          )}
        </div>
      );
    }

    return this.props.children;
  }
}

export default ErrorBoundary;
